import { useContext } from "react";

import { DialogContext } from "./DialogContext";
import { DialogContextValue } from "./types";

/**
 * Hook to access dialog management functions. Requires the `DialogProvider`
 *
 * @throws {Error} If used outside of DialogProvider
 *
 * @example
 * ```tsx
 * const { openDialog, closeDialog, isDialogOpen } = useDialogs<'confirm' | 'edit'>();
 *
 * const handleConfirm = () => {
 *   openDialog('confirm', { message: 'Are you sure?' });
 * };
 *
 * const handleEdit = () => {
 *   openDialog('edit', { itemId: 123 });
 * };
 * ```
 */
export const useDialogs = <
  TDialogNames extends string = string,
>(): DialogContextValue<TDialogNames> => {
  const context = useContext<DialogContextValue<TDialogNames> | undefined>(
    DialogContext,
  );

  if (!context) {
    throw new Error("useDialogs must be used within a DialogProvider");
  }

  return context;
};
